// src/utils/offertaFormazione.js
// ═══════════════════════════════════════════════════════════════════════════
// OFFERTE DI FORMAZIONE DEL PLATFORM OWNER (ottobre 2026, W430, regole 69)
// ═══════════════════════════════════════════════════════════════════════════
// Calcolo dell'offerta (prezzi in utils/pianiNexiora.js → FORMAZIONE, gli stessi del sito):
//  • importo della formula + ore aggiuntive (solo con un pacchetto);
//  • trasferta, solo in presenza (decisa con Paolo):
//      km andata e ritorno × €0,70; pedaggi al costo (stima, si fattura il costo effettivo);
//      tempo di viaggio incluso fino a 50 km per tratta; oltre si paga TUTTO il viaggio,
//      andata e ritorno, arrotondato alla mezz'ora più vicina, × €45/ora;
//      oltre 300 km vitto e alloggio al costo (stima facoltativa);
//  • IVA 22%; bonifico anticipato entro 7 giorni prima dell'incontro.
// Ogni numero ha accanto la spiegazione in parole: va nel PDF così il cliente capisce il conto.
// Nessun accesso al database: solo calcoli (prova demo/prove/prova_offerta_formazione.mjs).
// ═══════════════════════════════════════════════════════════════════════════
import { FORMAZIONE } from "./pianiNexiora";

const F = FORMAZIONE;
export const FORMULE_OFFERTA = {
  presenza: { label: `Pacchetto di formazione in presenza (${F.presenza.ore} ore)`, prezzo: F.presenza.prezzo, ore: F.presenza.ore, presenza: true, pacchetto: true },
  videocall: { label: `Pacchetto di formazione in video call (${F.videocall.ore} ore)`, prezzo: F.videocall.prezzo, ore: F.videocall.ore, presenza: false, pacchetto: true },
  ora: { label: "Ora singola di formazione in video call", prezzo: F.oraSingola, ore: 1, presenza: false, pacchetto: false },
};

const arrotonda2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
export const euroOfferta = (n) => "€ " + arrotonda2(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const numero = (v) => { const n = Number(String(v ?? "").replace(",", ".")); return Number.isFinite(n) && n > 0 ? n : 0; };
const oreMin = (min) => { const h = Math.floor(min / 60), m = Math.round(min % 60); return h ? `${h} h${m ? ` ${m} min` : ""}` : `${m} min`; };
const oreTesto = (ore) => `${String(ore).replace(".", ",")} ${ore === 1 ? "ora" : "ore"}`;

/**
 * @param o { formula, oreAggiuntive, kmAndata, minutiAndata, pedaggi, vittoAlloggio }
 * @returns { voci: [{ descrizione, dettaglio, importo }], spiegazioneTrasferta: [testo], avvisi: [testo], imponibile, iva, totale, ivaPerc }
 */
export function calcolaOfferta(o = {}) {
  const fo = FORMULE_OFFERTA[o.formula] || FORMULE_OFFERTA.presenza;
  const voci = [{ descrizione: fo.label, dettaglio: fo.pacchetto ? `Fino a ${F.maxPartecipanti} partecipanti, con la registrazione dell'incontro` : "", importo: fo.prezzo }];
  const avvisi = [];
  const extra = fo.pacchetto ? Math.min(20, Math.floor(numero(o.oreAggiuntive))) : 0;
  if (extra) voci.push({ descrizione: `Ore aggiuntive al pacchetto: ${extra} × ${euroOfferta(F.oraAggiuntiva)}`, dettaglio: `Da usare entro ${F.mesiOreAggiuntive} mesi, anche in più incontri`, importo: extra * F.oraAggiuntiva });
  const spiegazioneTrasferta = [];
  if (fo.presenza) {
    const km = numero(o.kmAndata), min = numero(o.minutiAndata), pedaggi = numero(o.pedaggi), vitto = numero(o.vittoAlloggio);
    if (!km) avvisi.push("Indica i km di sola andata fino alla sede del cliente.");
    const kmAR = arrotonda2(km * 2);
    const costoKm = arrotonda2(kmAR * F.euroKm);
    if (km) {
      voci.push({ descrizione: `Trasferta: ${String(kmAR).replace(".", ",")} km andata e ritorno × ${euroOfferta(F.euroKm)}/km`, dettaglio: "", importo: costoKm });
      spiegazioneTrasferta.push(`Distanza: ${String(km).replace(".", ",")} km per tratta, ${String(kmAR).replace(".", ",")} km andata e ritorno × ${euroOfferta(F.euroKm)} = ${euroOfferta(costoKm)}.`);
    }
    if (km > F.kmViaggioIncluso) {
      if (!min) avvisi.push(`La sede è oltre ${F.kmViaggioIncluso} km: indica il tempo di viaggio di sola andata.`);
      const minAR = min * 2;
      const ore = minAR ? Math.max(0.5, Math.round(minAR / 30) / 2) : 0;
      const costo = arrotonda2(ore * F.euroOraViaggio);
      if (ore) {
        voci.push({ descrizione: `Tempo di viaggio: ${oreTesto(ore)} × ${euroOfferta(F.euroOraViaggio)}/ora`, dettaglio: `Andata e ritorno ${oreMin(minAR)}, arrotondato alla mezz'ora più vicina`, importo: costo });
        spiegazioneTrasferta.push(`La sede è oltre ${F.kmViaggioIncluso} km, quindi il tempo di viaggio si paga per intero: ${oreMin(min)} per tratta, ${oreMin(minAR)} andata e ritorno, arrotondato alla mezz'ora più vicina = ${oreTesto(ore)} × ${euroOfferta(F.euroOraViaggio)} = ${euroOfferta(costo)}.`);
      }
    } else if (km) {
      spiegazioneTrasferta.push(`La sede è entro ${F.kmViaggioIncluso} km: il tempo di viaggio è incluso.`);
    }
    if (pedaggi) {
      voci.push({ descrizione: "Pedaggi autostradali (stima)", dettaglio: "Al costo: si fattura l'importo effettivo", importo: arrotonda2(pedaggi) });
      spiegazioneTrasferta.push(`Pedaggi autostradali: al costo. Stima andata e ritorno ${euroOfferta(pedaggi)}; si fattura l'importo effettivo.`);
    } else if (km) {
      spiegazioneTrasferta.push("Pedaggi autostradali, se presenti: al costo, si fattura l'importo effettivo.");
    }
    if (km > F.kmVittoAlloggio) {
      if (vitto) voci.push({ descrizione: "Vitto e alloggio (stima)", dettaglio: "Al costo: si fattura l'importo effettivo", importo: arrotonda2(vitto) });
      spiegazioneTrasferta.push(`Oltre ${F.kmVittoAlloggio} km: vitto e alloggio al costo${vitto ? `, stima ${euroOfferta(vitto)}` : ", a consuntivo"}; si fattura l'importo effettivo.`);
    }
  }
  const imponibile = arrotonda2(voci.reduce((t, v) => t + (v.importo || 0), 0));
  const iva = arrotonda2(imponibile * F.ivaPerc / 100);
  return { voci, spiegazioneTrasferta, avvisi, imponibile, iva, totale: arrotonda2(imponibile + iva), ivaPerc: F.ivaPerc };
}

/** Data entro cui pagare: 7 giorni prima dell'incontro ("AAAA-MM-GG"), o "" senza data. */
export function scadenzaPagamento(dataIncontro, giorni = F.giorniPagamentoAnticipato) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dataIncontro || ""))) return "";
  const [a, m, g] = dataIncontro.split("-").map(Number);
  const d = new Date(Date.UTC(a, m - 1, g - giorni));
  return d.toISOString().slice(0, 10);
}
export const dataItaliana = (s) => { const [a, m, g] = String(s || "").split("-"); return a && m && g ? `${g}/${m}/${a}` : ""; };

/** Numero dell'offerta: NXF-2026-0001 (Nexiora Formazione). */
export const numeroOfferta = (anno, n) => `NXF-${anno}-${String(n).padStart(4, "0")}`;

/** IBAN valido (controllo internazionale mod 97); accetta spazi. */
export function ibanValido(v) {
  const s = String(v || "").replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(s)) return false;
  if (s.startsWith("IT") && s.length !== 27) return false;
  const r = (s.slice(4) + s.slice(0, 4)).replace(/[A-Z]/g, c => String(c.charCodeAt(0) - 55));
  let resto = 0;
  for (const ch of r) resto = (resto * 10 + Number(ch)) % 97;
  return resto === 1;
}
export const ibanLeggibile = (v) => String(v || "").replace(/\s+/g, "").toUpperCase().replace(/(.{4})/g, "$1 ").trim();

/** Oggetto predefinito dell'offerta. */
export function oggettoPredefinito(formula, dataIncontro) {
  const fo = FORMULE_OFFERTA[formula] || FORMULE_OFFERTA.presenza;
  return `Offerta per la formazione su Nexiora – ${fo.label}${dataIncontro ? `, ${dataItaliana(dataIncontro)}` : ""}`;
}

/** Testo del pagamento per il PDF e l'email. */
export function testoPagamento(dataIncontro) {
  const sc = scadenzaPagamento(dataIncontro);
  return sc ? `Bonifico bancario anticipato entro il ${dataItaliana(sc)} (${F.giorniPagamentoAnticipato} giorni prima dell'incontro).`
    : `Bonifico bancario anticipato, almeno ${F.giorniPagamentoAnticipato} giorni prima dell'incontro.`;
}

/** Condizioni brevi in fondo all'offerta (stesse regole del sito e delle Condizioni, art. 10-bis). */
export function condizioniOfferta(validitaGiorni = F.validitaOffertaGiorni) {
  return [
    `Prezzi IVA esclusa. Offerta valida ${validitaGiorni} giorni dalla data di emissione.`,
    "Con l'accettazione dell'offerta la data resta riservata per voi ed emettiamo la fattura, da pagare entro la data indicata; ricevuto il pagamento, vi mandiamo la conferma con l'invito.",
    `La data confermata si può spostare gratuitamente fino a ${F.oreSpostamentoGratuito} ore prima.`,
    `Le ore aggiuntive si usano entro ${F.mesiOreAggiuntive} mesi dalla data del pacchetto.`,
    "Condizioni complete: nexiora.it/condizioni.html, articolo 10-bis (Formazione).",
  ];
}
